import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";

import type { ProductModuleCode } from "@/lib/product-module";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { useRoleDataScope } from "../commons/useRoleDataScope";

/**
 * Existe briefing mais novo do que a última vez que o usuário abriu "Meu Agente"?
 *
 * O briefing é entregue no CRM (agent_runs.detail.text), mas fica numa página
 * que ninguém visita por hábito — então nasce um ponto no menu.
 *
 * A marca de "lido" vive no localStorage, não no banco: é por dispositivo, e
 * pra "já vi o briefing de hoje" isso é suficiente e não custa migration nem
 * caminho de escrita. Se um dia precisar ser cross-device, vira coluna em
 * agent_instances.
 *
 * ATENÇÃO ao super admin: a RLS de agent_runs é "own OR super admin", então uma
 * consulta solta traria briefing dos OUTROS e acenderia o ponto por engano. O
 * filtro por user_id é obrigatório aqui, não é defesa redundante.
 *
 * A decisão de "li" mora AQUI, não na página: o hook vive no menu e o menu é
 * quem desenha o ponto. Deixar a página gravar o localStorage não funcionava —
 * o estado do hook já tinha sido calculado e nada o avisava, então o ponto só
 * sumia no reload seguinte.
 */

const CHAVE_VISTO = "north.briefingVistoEm";

const lerVisto = (): number => {
  try {
    return Number(window.localStorage.getItem(CHAVE_VISTO) ?? 0);
  } catch {
    return 0;
  }
};

export const marcarBriefingComoLido = () => {
  try {
    window.localStorage.setItem(CHAVE_VISTO, String(Date.now()));
  } catch {
    // localStorage bloqueado: o ponto só continua aparecendo, não quebra nada.
  }
};

/** O que o agente decidiu destacar hoje. Ver db/supabase/functions/chat-ai/briefing.ts (publicar_painel). */
export type PainelDoDia = {
  v: number;
  resumo: string;
  clima: 'urgente' | 'atencao' | 'tranquilo' | 'comemorar' | null;
  destaques: { bloco: string; motivo: string }[];
  acoes: { rotulo: string; rota: string }[];
  /** Indicadores do topo que o agente puxou pra frente hoje. */
  indicadores?: string[];
};

export type UltimoBriefing = {
  instanceId: number;
  agente: string;
  texto: string;
  quando: string;
  /** Ausente quando o agente não chamou publicar_painel — o dashboard cai na
   *  ordem estática, que é o comportamento correto e não um erro. */
  painel?: PainelDoDia | null;
  /** Produto do template (migration 106). Um briefing de Seguros no dashboard
   *  do Consórcio é dado certo no lugar errado. */
  produto: ProductModuleCode;
};

/**
 * O último briefing do usuário + se ele ainda não foi visto.
 *
 * Uma consulta só, usada em dois lugares: o ponto no menu e o card do
 * dashboard. Não vale duplicar a query — nem o cuidado com o super admin.
 */
export const useBriefingNaoLido = () => {
  const { currentCrmUserId, isLoading } = useRoleDataScope();
  const { pathname } = useLocation();
  const naPaginaDoAgente = pathname.startsWith("/meu-agente");
  const [naoLido, setNaoLido] = useState(false);
  const [briefings, setBriefings] = useState<UltimoBriefing[]>([]);

  const verificar = useCallback(async () => {
    if (isLoading || currentCrmUserId == null) return;

    // Um briefing POR AGENTE. Com .limit(1) o agente mais recente escondia o
    // outro — quem tem Vendas e Gestão via só um dos dois.
    const { data, error } = await supabase
      .from("agent_runs")
      .select(
        "instance_id, started_at, detail, " +
          "agent_instances!inner(user_id, display_name, template_slug, " +
          "agent_templates(name, product_module))",
      )
      .eq("agent_instances.user_id", currentCrmUserId)
      .eq("kind", "briefing")
      .eq("status", "done")
      .order("started_at", { ascending: false })
      .limit(20);

    if (error || !data?.length) return;

    type Linha = {
      instance_id: number;
      started_at: string;
      detail: { text?: string; painel?: PainelDoDia } | null;
      agent_instances: {
        display_name: string | null;
        template_slug: string;
        agent_templates: { name: string; product_module: string | null } | null;
      } | null;
    };

    const porInstancia = new Map<number, UltimoBriefing>();
    for (const run of data as unknown as Linha[]) {
      if (porInstancia.has(run.instance_id)) continue; // já veio ordenado desc
      const texto = run.detail?.text?.trim();
      if (!texto) continue;
      porInstancia.set(run.instance_id, {
        instanceId: run.instance_id,
        // O nome vem do TEMPLATE, não de um mapa de slugs escrito à mão. O mapa
        // antigo só conhecia dois slugs, então os dois agentes de Seguros
        // caíam no `else` e apareciam como "Vendas" — quatro briefings na tela,
        // dois deles com o nome errado e impossíveis de distinguir.
        agente:
          run.agent_instances?.display_name?.trim() ||
          run.agent_instances?.agent_templates?.name ||
          run.agent_instances?.template_slug ||
          "Agente",
        produto:
          run.agent_instances?.agent_templates?.product_module === "insurance"
            ? "insurance"
            : "consortium",
        texto,
        quando: run.started_at,
        // Vem de graça: a consulta já seleciona `detail` inteiro.
        painel: run.detail?.painel ?? null,
      });
    }

    const lista = [...porInstancia.values()].sort((a, b) => b.quando.localeCompare(a.quando));
    setBriefings(lista);

    const maisRecente = lista[0] ? new Date(lista[0].quando).getTime() : NaN;
    setNaoLido(Number.isFinite(maisRecente) && maisRecente > lerVisto());
  }, [currentCrmUserId, isLoading]);

  useEffect(() => {
    void verificar();
  }, [verificar]);

  // Estar na página É o evento de leitura. Some do menu na hora, sem depender
  // de a página avisar.
  useEffect(() => {
    if (!naPaginaDoAgente) return;
    marcarBriefingComoLido();
    setNaoLido(false);
  }, [naPaginaDoAgente]);

  // O ponto só reaparece na próxima verificação (carga do app). O briefing é
  // diário, então não vale abrir subscription em tempo real por isso.
  return { naoLido: naoLido && !naPaginaDoAgente, briefings };
};
